import { currentDayStartHour, keyToDate, todayKey } from '@/lib/day'
import { dueOnDate } from '@/lib/schedule'

// Minutes since midnight for a "HH:MM" time.
export function toMinutes(time: string): number {
  const [h = 0, m = 0] = time.split(':').map(Number)
  return h * 60 + m
}

// Shift a "HH:MM" time by a number of minutes, wrapping within a day.
export function shiftTime(time: string, deltaMin: number): string {
  const [h = 9, m = 0] = time.split(':').map(Number)
  const total = (((h * 60 + m + deltaMin) % 1440) + 1440) % 1440
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

// Coerce a picked end time: empty or earlier-than-start falls back to start + 1hr;
// otherwise the picked value is kept as-is.
export function coerceEnd(start: string, rawEnd: string): string {
  if (!rawEnd) return shiftTime(start, 60)
  return toMinutes(rawEnd) < toMinutes(start) ? shiftTime(start, 60) : rawEnd
}

// Whether a recurrence with the given period/day recurs on today's local date.
export function recursOnToday(
  period: string | null | undefined,
  recurrenceDay?: number | null,
  recursDays?: readonly number[] | null,
): boolean {
  if (!period) return false
  return dueOnDate(period, recurrenceDay, keyToDate(todayKey()), recursDays)
}

// Whether the current moment is past a recurring occurrence's end
// (start "HH:MM" + duration) within the current logical day. Measured relative
// to the user's day-start hour so a late-night occurrence stays overdue until
// the day rolls over (matching the "done today" boundary), instead of resetting
// at calendar midnight.
export function recurrenceOverdue(
  recurrenceTime: string | null | undefined,
  durationMinutes?: number | null,
  dayStartHour: number = currentDayStartHour(),
): boolean {
  if (!recurrenceTime) return false
  const ds = dayStartHour * 60
  const now = new Date()
  const nowSinceDayStart = (((now.getHours() * 60 + now.getMinutes() - ds) % 1440) + 1440) % 1440
  const startSinceDayStart = (((toMinutes(recurrenceTime) - ds) % 1440) + 1440) % 1440
  return nowSinceDayStart > startSinceDayStart + (durationMinutes ?? 60)
}

// A due date is "urgent" (rendered red) when it falls on today or earlier —
// due-today should read red even before its time passes, not only once overdue.
export function isDueTodayOrOverdue(ms: number | null | undefined): boolean {
  if (ms == null) return false
  const now = new Date()
  const endOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59,
    999,
  ).getTime()
  return ms <= endOfToday
}

// Default start/end for a newly-timed task: the next full hour in the user's
// local time (e.g. at 15:15 → start "16:00", end "17:00").
export function nextHourStart(): string {
  const h = (new Date().getHours() + 1) % 24
  return `${String(h).padStart(2, '0')}:00`
}
export function nextHourEnd(): string {
  const h = (new Date().getHours() + 2) % 24
  return `${String(h).padStart(2, '0')}:00`
}

// Whether a dated task is actually overdue (drives the red "Overdue" label).
// A timed task is overdue once its timestamp passes; an all-day task is overdue
// only once its whole day is in the past (its midnight stamp is "before now"
// all day today, which must NOT read as overdue).
export function isTaskOverdue(ms: number | null | undefined, allDay?: boolean): boolean {
  if (ms == null) return false
  if (!allDay) return ms < Date.now()
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  return ms < startOfToday
}

// Compact display label for a "HH:MM" time: strip the leading zero on the hour
// but always keep the minutes (e.g. "09:00" → "9:00", "14:05" → "14:05").
export function formatTimeLabel(time: string): string {
  const [h = 0, m = 0] = time.split(':').map(Number)
  return `${h}:${String(m).padStart(2, '0')}`
}
